import type { ServiceSection } from "@/components/ServiceSections";

export interface PlumbingServiceContent {
  title: string;
  metaDesc: string;
  heroDesc: string;
  benefits: string[];
  faqs: { question: string; answer: string }[];
  sections: ServiceSection[];
}

export const plumbingContent: Record<string, PlumbingServiceContent> = {
  "emergency-plumbing": {
    title: "Tolleson Emergency Plumber — Fast Response, Licensed & Insured",
    metaDesc:
      "Fast response emergency plumber in Tolleson, AZ. Burst pipes, major leaks, sewer backups, monsoon flood damage. Licensed & insured. Call (480) 322-2567 now.",
    heroDesc:
      "Pipes don't wait for business hours to burst. When water is pouring into your home at 2 a.m. or a sewer backup threatens your family's health, you need a licensed plumber who answers the phone and shows up fast. Tru American Plumbing serves Tolleson and the entire West Valley with rapid-response emergency service.",
    benefits: [
      "Rapid dispatch to Tolleson and surrounding cities",
      "Fully stocked trucks — most repairs completed in one visit",
      "Upfront pricing even at midnight — no surprise overtime fees",
      "Licensed, bonded, and insured (ROC 362827)",
    ],
    faqs: [
      {
        question: "What qualifies as a plumbing emergency?",
        answer:
          "Burst pipes, active flooding, sewer backups, gas line leaks, no water service, and overflowing fixtures all qualify. If water is actively damaging your home or you smell gas, call immediately.",
      },
      {
        question: "How quickly can you get to my Tolleson home?",
        answer:
          "Because we're headquartered in Tolleson, we typically reach local emergencies faster than competitors driving from Phoenix or Scottsdale. Exact times depend on current dispatch, but we prioritize active-damage calls.",
      },
      {
        question: "Do you charge extra for nights and weekends?",
        answer:
          "We provide upfront pricing before we start any work. You'll know the cost before we turn a wrench, regardless of what time it is.",
      },
    ],
    sections: [
      {
        type: "warnings",
        heading: "Signs You Need an Emergency Plumber Now",
        items: [
          "Water gushing from a pipe, fixture, or ceiling",
          "Sewage backing up into your home through drains or toilets",
          "Strong smell of natural gas near water heater or gas lines",
          "No water at all from any faucet in the house",
          "Water pooling around your foundation or in your yard",
          "A water heater leaking or making popping/banging sounds",
        ],
      },
      {
        type: "prose",
        heading: "Why Emergency Plumbing Hits Different in Arizona",
        paragraphs: [
          "Arizona's extreme heat puts unique stress on your plumbing. Copper pipes expand and contract through 40-degree daily temperature swings, loosening joints over time. Desert soil shifts seasonally, stressing underground lines. And when monsoon season rolls through the West Valley every July through September, sudden flooding can overwhelm sewer systems and push debris into drain lines.",
          "Tolleson and surrounding cities like Avondale and Goodyear sit in the Salt River floodplain. Homes here face higher risk of storm-related backups and foundation-level water intrusion during heavy rains. A plumber who understands West Valley geography responds smarter — we know which neighborhoods flood first and what infrastructure issues to look for.",
          "We've handled emergencies in original Tolleson homes with 50-year-old galvanized pipes and in brand-new Goodyear builds with construction defects. Whatever the era of your plumbing, we've seen the failure mode and know how to fix it fast.",
        ],
      },
      {
        type: "tip",
        heading: "What to Do While Waiting for Your Emergency Plumber",
        text: "Shut off your main water valve immediately — it's usually near the street or where the water line enters your home. If you smell gas, leave the house, don't flip any switches, and call both your gas company and us. For sewer backups, stop using all water in the home. Knowing your shutoff valve location before an emergency can save thousands in water damage.",
      },
    ],
  },

  "slab-leak-detection": {
    title: "Slab Leak Detection in Tolleson, AZ — Find It Before It Spreads",
    metaDesc:
      "Expert slab leak detection in Tolleson, AZ. Advanced acoustic and thermal technology finds leaks under concrete without demolition. Call (480) 322-2567.",
    heroDesc:
      "A slab leak can silently destroy your foundation, warp your flooring, and spike your water bill for months before you notice. Our detection technology pinpoints the exact location under your concrete slab so we fix the leak — not your entire floor.",
    benefits: [
      "Non-invasive acoustic and thermal detection",
      "Pinpoint accuracy reduces excavation area by up to 90%",
      "Prevent foundation damage that costs $5,000–$15,000+ to repair",
      "Licensed specialists with Arizona slab construction experience",
      "Written report with findings and repair options",
    ],
    faqs: [
      {
        question: "What are signs of a slab leak?",
        answer:
          "Unexplained water bill increases, warm or damp spots on floors, the sound of running water when nothing is on, cracks in walls or flooring, mold smell, and low water pressure are common indicators.",
      },
      {
        question: "How do you detect leaks under concrete?",
        answer:
          "We use acoustic listening equipment to hear the sound of pressurized water escaping pipes, combined with thermal imaging to detect temperature differences in the slab. Pressure testing confirms the leak before we pinpoint its location.",
      },
      {
        question: "Can slab leaks cause foundation damage?",
        answer:
          "Yes. Water erodes the soil supporting your foundation, causing settling and cracks. Arizona's expansive clay soils make this worse — wet clay swells, dry clay shrinks, and the cycle cracks foundations. Early detection saves thousands.",
      },
    ],
    sections: [
      {
        type: "stats",
        items: [
          { value: "$8,500+", label: "Avg. foundation repair cost in AZ" },
          { value: "300 gal", label: "Water lost per day from a typical slab leak" },
          { value: "90%", label: "Less excavation with pinpoint detection" },
          { value: "24 hrs", label: "Typical detect-to-repair timeline" },
        ],
      },
      {
        type: "process",
        heading: "Our 3-Step Slab Leak Detection Process",
        steps: [
          {
            title: "Pressure Test",
            desc: "We isolate your hot and cold water lines and test each system for pressure loss, confirming a leak exists and which line is affected.",
          },
          {
            title: "Acoustic & Thermal Scan",
            desc: "Using professional-grade listening equipment and infrared cameras, we scan your slab to pinpoint the leak's exact location — often within inches.",
          },
          {
            title: "Report & Repair Options",
            desc: "You receive a clear explanation of the problem with repair options ranging from spot repair to reroute, along with upfront pricing for each.",
          },
        ],
      },
      {
        type: "prose",
        heading: "Arizona Soil and Your Foundation",
        paragraphs: [
          "The West Valley sits on expansive clay soil — the kind that swells when wet and shrinks when dry. This natural cycle creates constant movement under your home's slab foundation. When a copper water line under that slab develops a pinhole leak, the escaping water saturates the clay around it, causing localized swelling that pushes up on the slab unevenly.",
          "Over months, this uneven pressure cracks tile, warps flooring, and can fracture the slab itself. We've seen Tolleson homes where a small slab leak turned into a $12,000 foundation repair because it went undetected for a year. The lesson: if your water bill jumps $30–50 unexpectedly, don't ignore it.",
          "Our team understands Arizona slab construction — post-tension vs. conventional, copper vs. PEX repipes — and we tailor our detection and repair approach to your home's specific build.",
        ],
      },
    ],
  },

  "drain-cleaning": {
    title: "Drain Cleaning Near Tolleson — Same-Day Service Available",
    metaDesc:
      "Professional drain cleaning in Tolleson, AZ. Hydro jetting, camera inspections, and same-day clog removal. No harsh chemicals. Call (480) 322-2567.",
    heroDesc:
      "Slow drains don't fix themselves — they get worse. Arizona's hard water deposits minerals inside your pipes year after year, narrowing the opening until a small clog becomes a full backup. Our professional drain cleaning restores full flow and shows you exactly what's happening inside your pipes.",
    benefits: [
      "Same-day service for Tolleson and West Valley",
      "Camera inspection included with cleaning",
      "Hydro jetting for stubborn buildup",
      "Safe for all pipe materials — no chemical damage",
    ],
    faqs: [
      {
        question: "Why do my drains keep clogging?",
        answer:
          "Arizona's hard water leaves mineral deposits inside pipes, narrowing them over time. Combined with soap scum, hair, or grease, these deposits create recurring clogs. Professional cleaning removes the buildup that store-bought products can't reach.",
      },
      {
        question: "Is hydro jetting safe for older pipes?",
        answer:
          "We camera-inspect your pipes before jetting to check their condition. For older or fragile pipes, we adjust pressure or use mechanical cleaning. We never jet a pipe that can't handle it.",
      },
      {
        question: "How often should I get my drains cleaned?",
        answer:
          "In Arizona, every 12–18 months for preventive maintenance. If you have older pipes or frequently use your garbage disposal, annual cleaning prevents emergency backups.",
      },
    ],
    sections: [
      {
        type: "comparison",
        heading: "DIY vs. Professional Drain Cleaning",
        optionA: {
          title: "Store-Bought Solutions",
          points: [
            "Chemical drain cleaners corrode pipes over time",
            "Only dissolves clogs near the drain opening",
            "Can't remove mineral scale or root intrusion",
            "Temporary fix — clogs return within weeks",
            "Risk of pipe damage in older homes",
          ],
        },
        optionB: {
          title: "Professional Cleaning",
          points: [
            "Hydro jetting removes 100% of buildup",
            "Camera inspection diagnoses the actual problem",
            "Clears the entire line, not just the opening",
            "Results last 1–2 years with proper care",
            "Safe for all pipe types when done by a pro",
          ],
        },
      },
      {
        type: "prose",
        heading: "Hard Water and Your Drains — An Arizona Problem",
        paragraphs: [
          "The West Valley's municipal water supply runs 15–25 grains per gallon of hardness — more than triple the national average. Those dissolved minerals don't just leave spots on your dishes. They coat the inside of your drain pipes with a chalite-like scale that narrows the pipe opening by millimeters each year.",
          "After five to ten years, a 2-inch drain pipe can effectively become a 1-inch pipe. Now every bit of hair, soap, or food scrap that would have washed through gets caught. That's why Arizona homes experience more frequent clogs than homes in softer-water regions — it's not just what goes down the drain, it's what's already coating the walls.",
          "Professional cleaning with hydro jetting strips that mineral layer down to bare pipe, restoring full diameter. Combined with a camera inspection, we can show you the before and after on screen.",
        ],
      },
      {
        type: "process",
        heading: "How We Clean Your Drains",
        steps: [
          {
            title: "Camera Inspection",
            desc: "We run a waterproof camera through your drain to see the clog type, pipe condition, and any damage before we begin.",
          },
          {
            title: "Professional Cleaning",
            desc: "Using hydro jetting or mechanical snaking (based on pipe condition), we clear the entire line — not just the area near the drain.",
          },
          {
            title: "Verify & Advise",
            desc: "A follow-up camera pass confirms the line is clear. We'll advise on any pipe concerns and how to keep drains flowing.",
          },
        ],
      },
    ],
  },

  "leak-detection-repair": {
    title: "Find Hidden Leaks Fast — Tolleson Leak Detection Experts",
    metaDesc:
      "Hidden leak detection in Tolleson, AZ. Advanced equipment finds leaks behind walls, under floors, and in yards. Stop water waste. Call (480) 322-2567.",
    heroDesc:
      "A hidden leak can waste 10,000+ gallons per year and cause mold, structural damage, and sky-high water bills. Our non-invasive detection equipment finds leaks behind walls, under slabs, and in your yard — without tearing your home apart.",
    benefits: [
      "Non-invasive acoustic and thermal detection",
      "Find leaks behind walls without cutting drywall",
      "Yard and irrigation leak detection",
      "Repair the same day in most cases",
    ],
    faqs: [
      {
        question: "How do I know if I have a hidden leak?",
        answer:
          "Check your water meter with all fixtures off. If it's still moving, you have a leak. Other signs: unexplained bill increases, musty smells, warm spots on floors, bubbling paint, or sounds of running water.",
      },
      {
        question: "Can you find leaks without cutting into walls?",
        answer:
          "Yes. Our acoustic equipment detects the sound of pressurized water escaping pipes, and thermal cameras show temperature differences caused by moisture. Most leaks are located without any demolition.",
      },
    ],
    sections: [
      {
        type: "warnings",
        heading: "Warning Signs of a Hidden Leak",
        items: [
          "Water bill spiked $30+ with no usage change",
          "Water meter keeps spinning with everything off",
          "Warm or damp spots on floors (especially on slab)",
          "Musty or mildew smell in rooms without obvious moisture",
          "Bubbling, peeling, or discolored paint on walls",
          "Sound of running water when no fixtures are on",
        ],
      },
      {
        type: "prose",
        heading: "Desert Homes Lose Thousands of Gallons Unnoticed",
        paragraphs: [
          "In a city where water is precious and expensive, hidden leaks are more than an inconvenience — they're a financial drain. The average hidden leak wastes 10,000 gallons per year. In Tolleson, where water rates have increased steadily, that's real money flowing into the ground.",
          "Arizona's dry climate masks leak symptoms that would be obvious in humid states. A leak behind a wall in Florida produces visible mold within days. In Arizona, that same leak can run for months before moisture migrates far enough to show. By then, the drywall, framing, and insulation may all need replacement.",
          "We see this pattern repeatedly in West Valley homes: a homeowner notices their bill creeping up, assumes it's rate increases, and discovers six months later that a pinhole leak has been saturating their wall cavity. Early detection with professional equipment catches these leaks before damage compounds.",
        ],
      },
      {
        type: "tip",
        heading: "The Overnight Meter Test",
        text: "Before bed, note your water meter reading. Don't use any water overnight — no toilets, ice makers, or irrigation. Check the meter first thing in the morning. If the reading changed, you have a leak somewhere in your system. Call us for a professional detection to find exactly where.",
      },
    ],
  },

  "water-heater-repair-installation": {
    title: "Water Heater Repair & Installation in Tolleson, AZ",
    metaDesc:
      "Water heater repair, replacement, and tankless installation in Tolleson, AZ. Tank and tankless experts. Same-day service available. Call (480) 322-2567.",
    heroDesc:
      "No hot water? Rusty water coming from your faucets? Strange noises from the garage? Your water heater is one of the hardest-working appliances in your Arizona home — and when it fails, life stops. We repair all brands and install energy-efficient replacements, including tankless systems.",
    benefits: [
      "Same-day water heater repair for most issues",
      "Tank and tankless installation expertise",
      "Energy-efficient upgrades that lower utility bills",
      "All major brands serviced and installed",
    ],
    faqs: [
      {
        question: "How long do water heaters last in Arizona?",
        answer:
          "Tank water heaters typically last 6–10 years in Arizona (shorter than the national 8–12 year average) because our hard water accelerates sediment buildup and anode rod corrosion. Tankless units last 15–20 years with regular descaling.",
      },
      {
        question: "Should I switch to tankless?",
        answer:
          "Tankless works well for many Arizona homes — endless hot water, smaller footprint, and 20+ year lifespan. But they cost more upfront and may need gas line or electrical upgrades. We'll help you weigh the numbers for your specific situation.",
      },
      {
        question: "Why is my water heater making popping noises?",
        answer:
          "Popping or rumbling sounds mean sediment has accumulated at the bottom of the tank. Hard water accelerates this in Arizona. The sound is water boiling under the sediment layer. A flush may fix it, but in severe cases the tank needs replacement.",
      },
    ],
    sections: [
      {
        type: "comparison",
        heading: "Tank vs. Tankless Water Heaters in Arizona",
        optionA: {
          title: "Traditional Tank",
          points: [
            "Lower upfront cost ($1,200–$2,500 installed)",
            "Simple, well-understood technology",
            "Works with existing gas/electric setup",
            "6–10 year lifespan in AZ hard water",
            "Standby energy loss heating stored water",
          ],
        },
        optionB: {
          title: "Tankless (On-Demand)",
          points: [
            "Higher upfront ($2,800–$5,000 installed)",
            "Unlimited hot water — never runs out",
            "15–20 year lifespan with maintenance",
            "30% energy savings — no standby loss",
            "Compact wall-mount frees up floor space",
          ],
        },
      },
      {
        type: "prose",
        heading: "Why Arizona Is Different for Water Heaters",
        paragraphs: [
          "Your water heater operates in conditions most manufacturers don't design for. In summer, your incoming water temperature can be 90°F — meaning the heater barely has to work. In winter, it drops to 55°F, and the unit suddenly has to work twice as hard. This seasonal swing stresses components differently than in moderate climates.",
          "Then there's the water itself. At 15–25 grains per gallon, West Valley water deposits a thick layer of calcium and magnesium sediment in the bottom of tank heaters every year. Without annual flushing, that sediment layer grows until it insulates the burner from the water, making the unit work harder, run hotter, and fail sooner.",
          "Tankless units aren't immune — Arizona hard water can scale heat exchangers within 2–3 years without descaling. We recommend annual maintenance for any water heater in Arizona, and we offer service plans to make that easy.",
        ],
      },
      {
        type: "stats",
        items: [
          { value: "30%", label: "Energy savings with tankless" },
          { value: "6–10 yr", label: "Tank lifespan in AZ (vs. 12 national)" },
          { value: "20+ yr", label: "Tankless lifespan with maintenance" },
          { value: "90°F", label: "Summer inlet water temp in AZ" },
        ],
      },
    ],
  },

  "sewer-line-repair-replacement": {
    title: "Sewer Line Repair in Tolleson — Trenchless Options Available",
    metaDesc:
      "Sewer line repair and replacement in Tolleson, AZ. Camera inspections, trenchless pipe lining, root removal. Licensed sewer specialists. Call (480) 322-2567.",
    heroDesc:
      "Sewer problems are urgent, unpleasant, and potentially hazardous. Whether it's a root intrusion, a collapsed line, or a blockage from decades of buildup, we diagnose the problem with camera technology and offer trenchless repair options that save your yard and your budget.",
    benefits: [
      "HD camera inspections — see the problem yourself",
      "Trenchless pipe lining and pipe bursting options",
      "Root intrusion removal and prevention",
      "Full line replacement when necessary",
    ],
    faqs: [
      {
        question: "What are signs of sewer line problems?",
        answer:
          "Multiple drains backing up simultaneously, gurgling sounds from toilets, sewage odors in your home or yard, unusually green patches of grass, and slow drains throughout the house (not just one fixture).",
      },
      {
        question: "What is trenchless sewer repair?",
        answer:
          "Trenchless methods repair or replace your sewer line through small access points, without digging a trench across your yard. Options include pipe lining (epoxy coating inside the existing pipe) and pipe bursting (pulling a new pipe through the old one).",
      },
      {
        question: "How long does sewer line replacement take?",
        answer:
          "Trenchless repairs typically complete in one day. Traditional excavation may take 2–3 days depending on depth and access. We'll give you a timeline before we start.",
      },
    ],
    sections: [
      {
        type: "process",
        heading: "How We Diagnose and Repair Sewer Lines",
        steps: [
          {
            title: "Camera Inspection",
            desc: "A high-definition camera travels the full length of your sewer line, recording condition, blockages, root intrusion, and pipe material. You watch the feed with us.",
          },
          {
            title: "Diagnosis & Options",
            desc: "Based on the inspection, we explain the problem and present repair options — spot repair, trenchless lining, pipe bursting, or traditional replacement — with pricing for each.",
          },
          {
            title: "Repair & Verify",
            desc: "We complete the repair and run the camera again to verify the line is clear and structurally sound. You get before-and-after footage for your records.",
          },
        ],
      },
      {
        type: "warnings",
        heading: "Red Flags That Your Sewer Line Needs Attention",
        items: [
          "Multiple drains in the house back up at the same time",
          "Toilets gurgle when you run the washing machine",
          "Sewage smell inside the home or near the yard",
          "Unexplained wet or sunken spots in the yard",
          "Patches of grass that are greener than the rest",
          "Recurring backups despite repeated drain cleaning",
        ],
      },
      {
        type: "prose",
        heading: "Older Tolleson Homes and Sewer Line Risk",
        paragraphs: [
          "Original homes in the Tolleson area — built from the 1960s through the 1980s — often have clay tile or Orangeburg (tar paper) sewer lines. Clay tile joints separate over time, allowing root intrusion and soil infiltration. Orangeburg pipes, common in budget construction, literally decompose in the ground after 30–50 years.",
          "If your Tolleson home was built before 1990 and you've never had a sewer camera inspection, it's worth doing as a preventive measure. We routinely find root masses, joint separations, and partial collapses in homes where the owner had no symptoms — yet. Catching these issues before a full backup saves an emergency call and potential interior damage.",
          "For newer homes in Avondale, Goodyear, and Surprise, we see a different issue: construction debris left in sewer lines during building, and improperly graded pipes that allow standing water. A camera inspection after your builder warranty expires is a smart investment.",
        ],
      },
    ],
  },

  "toilet-repair-installation": {
    title: "Toilet Repair & Installation — Tolleson, AZ Plumber",
    metaDesc:
      "Toilet repair, replacement, and installation in Tolleson, AZ. Running toilets, clogs, upgrades to water-saving models. Call (480) 322-2567.",
    heroDesc:
      "A running toilet doesn't just make noise — it silently adds $50–100+ to your monthly water bill. A single running toilet can waste over 200 gallons per day. In Arizona, where water is expensive and every gallon matters, that's money down the drain. We repair all toilet issues and install water-efficient replacements.",
    benefits: [
      "Same-day toilet repair for most issues",
      "WaterSense-certified toilet installations",
      "All brands and models serviced",
      "Upfront pricing — no surprises",
    ],
    faqs: [
      {
        question: "How much water does a running toilet waste?",
        answer:
          "A running toilet can waste 200+ gallons per day — that's over 6,000 gallons per month. In Tolleson, that adds $50–100 to your water bill. Most running toilets are a simple flapper or fill valve fix.",
      },
      {
        question: "Should I repair or replace my toilet?",
        answer:
          "If your toilet is over 15 years old, constantly clogs, or uses 3.5+ gallons per flush, replacement with a WaterSense model (1.28 gpf) saves money long-term. We'll help you decide based on your specific situation.",
      },
    ],
    sections: [
      {
        type: "prose",
        heading: "The Real Cost of a Running Toilet in Arizona",
        paragraphs: [
          "Water isn't cheap in the West Valley, and it's getting more expensive. Tolleson, Avondale, and Goodyear have all increased water rates in recent years. A running toilet that wastes 200 gallons a day adds up to 6,000 gallons a month — enough to spike your bill by $50–100 depending on your tier.",
          "Most running toilets are caused by a worn flapper valve (a $5 part) or a failing fill valve. The repair takes 15–30 minutes. But many homeowners don't realize the toilet is running continuously, especially if it's a slow leak into the bowl. Put a few drops of food coloring in the tank — if color appears in the bowl within 15 minutes without flushing, your flapper is leaking.",
        ],
      },
      {
        type: "stats",
        items: [
          { value: "200+", label: "Gallons wasted per day (running toilet)" },
          { value: "1.28 gpf", label: "WaterSense-certified flush rating" },
          { value: "13,000", label: "Gallons saved per year per toilet upgrade" },
          { value: "$100+", label: "Monthly water bill savings potential" },
        ],
      },
      {
        type: "tip",
        heading: "Water Utility Rebates for Toilet Upgrades",
        text: "Many West Valley water utilities offer rebates for replacing old toilets with WaterSense-certified models. The City of Phoenix offers up to $75 per toilet, and several West Valley cities have similar programs. Ask us about available rebates in your area — we handle installations that qualify.",
      },
    ],
  },

  "faucet-fixture-repair-installation": {
    title: "Faucet & Fixture Repair in Tolleson — Stop Drips, Save Water",
    metaDesc:
      "Faucet repair, fixture installation, and hard water damage repair in Tolleson, AZ. Stop drips, upgrade finishes. Call (480) 322-2567.",
    heroDesc:
      "Arizona's hard water is brutal on faucets and fixtures. Calcium and mineral deposits crust over aerators, corrode cartridges, and destroy finishes years ahead of their rated lifespan. We repair and replace faucets, showerheads, and fixtures — and help you choose finishes that survive West Valley water.",
    benefits: [
      "All faucet brands and types serviced",
      "Hard-water-resistant fixture recommendations",
      "Precision installation — no leaks, no callbacks",
      "Upgrade outdated fixtures for water savings",
    ],
    faqs: [
      {
        question: "Why do my faucets keep crusting over?",
        answer:
          "Arizona's water is 15–25 grains per gallon hard. Calcium and magnesium deposits (white or greenish crust) build up on aerators, handles, and spouts. A water softener reduces this dramatically, and some fixture finishes resist buildup better than others.",
      },
      {
        question: "Which faucet finishes last longest in Arizona?",
        answer:
          "Brushed nickel and stainless steel hide mineral spots better than chrome. PVD (Physical Vapor Deposition) finishes — like brushed gold or matte black PVD — resist corrosion and spotting much longer in hard water environments.",
      },
    ],
    sections: [
      {
        type: "prose",
        heading: "Hard Water vs. Your Fixtures — An Arizona Battle",
        paragraphs: [
          "In states with soft water, a quality faucet lasts 15–20 years. In Arizona, we routinely replace faucets that are 5–7 years old because hard water mineral deposits have destroyed the cartridge, corroded the finish, and clogged the aerator beyond cleaning.",
          "The white and greenish crust you see on faucets and showerheads is calcium carbonate and magnesium — the same minerals that scale your water heater and narrow your drain pipes. Every time water sits on a fixture surface and evaporates, it leaves a thin layer of minerals behind. Day after day, that layer builds.",
          "A whole-home water softener dramatically reduces this problem. But if you're not ready for a softener, choosing the right fixture finish matters. We help clients select finishes rated for hard water environments, and we install them properly to prevent premature failure.",
        ],
      },
      {
        type: "warnings",
        heading: "Signs Your Fixtures Need Professional Attention",
        items: [
          "White or green mineral crust that won't scrub off",
          "Reduced water flow from the faucet or showerhead",
          "Stiff or hard-to-turn handles",
          "Dripping that continues after the handle is fully closed",
          "Visible corrosion or pitting on the fixture surface",
          "Leaking from the base of the faucet",
        ],
      },
      {
        type: "tip",
        heading: "Fixture Finishes That Resist Arizona Hard Water",
        text: "When replacing fixtures, look for PVD (Physical Vapor Deposition) finishes — brushed nickel PVD, matte black PVD, or brushed gold PVD. These finishes are bonded at the molecular level, making them significantly more resistant to hard water corrosion and spotting compared to standard chrome plating.",
      },
    ],
  },

  "garbage-disposal": {
    title: "Garbage Disposal Repair & Installation — Tolleson Plumber",
    metaDesc:
      "Garbage disposal repair and installation in Tolleson, AZ. Jams, leaks, replacements. All brands serviced. Call (480) 322-2567.",
    heroDesc:
      "Your garbage disposal handles a lot — maybe too much. From citrus season overloads to hard water corrosion, Arizona disposals take a beating. Whether yours is jammed, leaking, or just not grinding anymore, we repair and replace all brands and sizes.",
    benefits: [
      "All major brands repaired and installed",
      "Right-sized units for your household",
      "Proper electrical and plumbing connections",
      "Disposal and drain line optimization",
    ],
    faqs: [
      {
        question: "What size disposal do I need?",
        answer:
          "For most households, a 1/2 HP disposal handles daily use well. Families that cook frequently or have 4+ people benefit from a 3/4 HP model. We recommend InSinkErator and Waste King for Arizona homes.",
      },
      {
        question: "Why does my disposal smell bad?",
        answer:
          "Food residue trapped in the splash guard and grinding chamber decomposes in Arizona's heat faster than in cooler climates. Running cold water for 15 seconds after grinding, and periodically grinding ice cubes with citrus peel, helps. We can also deep-clean during a service call.",
      },
    ],
    sections: [
      {
        type: "prose",
        heading: "Disposals and Arizona Citrus Season",
        paragraphs: [
          "Every winter, Arizona homeowners harvest oranges, lemons, and grapefruits by the bagful. And every January through March, we see a spike in garbage disposal calls. Citrus peels, while they smell great, are fibrous and tough on disposal blades. Large quantities can wrap around the grinding plate, jam the motor, and overwhelm the drain line.",
          "The fix isn't to avoid your disposal — it's to use it wisely. Feed citrus peels in small amounts with plenty of cold water. Never stuff a whole orange worth of rind down at once. And if your disposal is a lower-powered 1/3 HP model, it may simply lack the torque for heavy citrus grinding. A 3/4 HP upgrade handles it easily.",
        ],
      },
      {
        type: "comparison",
        heading: "Disposal Size: 1/3 HP vs. 3/4 HP",
        optionA: {
          title: "1/3 HP (Builder Grade)",
          points: [
            "Handles light food scraps",
            "Struggles with fibrous vegetables and citrus peel",
            "Prone to jamming under heavy use",
            "Shorter lifespan (3–5 years typical)",
            "Usually included by home builders",
          ],
        },
        optionB: {
          title: "3/4 HP (Recommended Upgrade)",
          points: [
            "Grinds all common food waste including citrus",
            "Multi-stage grinding reduces clogs",
            "Faster processing, less water waste",
            "8–12 year lifespan",
            "Quieter operation with insulated housing",
          ],
        },
      },
      {
        type: "warnings",
        heading: "Garbage Disposal Do's and Don'ts",
        items: [
          "DO run cold water before, during, and after grinding",
          "DON'T pour grease or oil into the disposal",
          "DO grind small amounts at a time, not huge loads",
          "DON'T put bones, shellfish shells, or corn husks in",
          "DO use ice cubes periodically to clean the blades",
          "DON'T put expandable foods like rice or pasta in large quantities",
        ],
      },
    ],
  },

  "water-pressure-problems": {
    title: "Low Water Pressure in Tolleson? We Fix It",
    metaDesc:
      "Low water pressure repair in Tolleson, AZ. Diagnose and fix pressure issues from PRV failure, pipe corrosion, and municipal supply problems. Call (480) 322-2567.",
    heroDesc:
      "Weak showers, slow-filling toilets, and faucets that barely trickle are more than annoying — they signal a real problem in your plumbing system. We diagnose the cause of low water pressure and fix it, whether it's a failing pressure regulator, corroded pipes, or a municipal supply issue.",
    benefits: [
      "Complete pressure diagnosis — not just guessing",
      "PRV (pressure reducing valve) testing and replacement",
      "Corroded pipe assessment for older homes",
      "Municipal supply issue identification",
    ],
    faqs: [
      {
        question: "What causes low water pressure?",
        answer:
          "Common causes include a failing pressure reducing valve (PRV), corroded or scale-blocked galvanized pipes, a partially closed main shutoff valve, municipal supply issues, or leaks reducing system pressure. We test systematically to find the real cause.",
      },
      {
        question: "What is a PRV and when does it need replacing?",
        answer:
          "A pressure reducing valve (PRV) reduces incoming city water pressure to a safe level for your home (typically 50–70 psi). PRVs last 7–12 years and can fail by either not reducing pressure (too high) or restricting it too much (too low). Replacement is a common fix for pressure problems.",
      },
    ],
    sections: [
      {
        type: "process",
        heading: "How We Diagnose Water Pressure Problems",
        steps: [
          {
            title: "Pressure Reading",
            desc: "We test incoming pressure from the street and compare it to pressure at fixtures throughout your home. This tells us whether the problem is external or internal.",
          },
          {
            title: "System Inspection",
            desc: "We check your PRV, main shutoff valve, pipe condition (galvanized vs. copper vs. PEX), and look for leaks or restrictions that reduce flow.",
          },
          {
            title: "Targeted Fix",
            desc: "Based on diagnosis, we replace the PRV, clear restrictions, repair leaks, or recommend repiping if corroded galvanized pipes are the culprit.",
          },
        ],
      },
      {
        type: "prose",
        heading: "West Valley Pressure — A Unique Challenge",
        paragraphs: [
          "The West Valley's geography creates water pressure inconsistencies that other parts of metro Phoenix don't face. Elevations change from Buckeye through Tolleson to Glendale, and municipal water systems work to maintain consistent pressure across these grades. Homes at higher elevations or at the end of distribution lines often receive lower pressure.",
          "Combine that with Arizona's hard water scaling the inside of pipes and pressure regulators, and you've got a recipe for weak pressure. We frequently see PRVs in West Valley homes that have completely scaled over internally within 5–7 years — well short of their expected lifespan.",
          "If your home was built in the 1980s or earlier with galvanized steel pipes, internal corrosion may have reduced pipe diameter by 50% or more. No amount of PRV adjustment fixes that — it's a repiping conversation. We'll show you the evidence and give you honest options.",
        ],
      },
      {
        type: "warnings",
        heading: "Signs of Water Pressure Problems",
        items: [
          "Shower feels weak even at full blast",
          "Multiple fixtures run poorly at the same time",
          "Washing machine takes forever to fill",
          "Sputtering or air bursts when you turn on faucets",
          "Toilets refill very slowly after flushing",
          "Pressure seems fine early morning but drops during the day",
        ],
      },
    ],
  },
};
